// FinanceDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Wallet, XCircle, Users } from "lucide-react";

import Header from "shared/ui/Header";
import { BASE_URL } from "shared/lib/config";

import FinanceOverview from "../components/tabs/FinanceOverview";
import ClientManagement from "../components/tabs/ClientManagement";

import InvoicePenjualan from "../components/tabs/InvoicePenjualan";
import SalesOrder from "../components/tabs/SalesOrder";
import InvoiceTidakAktif from "../components/tabs/InvoiceTidakAktif";

export default function FinanceDashboard() {
  const navigate = useNavigate();

  const allowedTabs = new Set(["overview", "clients", "sales-order", "penjualan", "tidak-aktif"]);

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const store = sessionStorage.getItem("idToken") ? sessionStorage : localStorage;
      const saved = store.getItem("financeActiveTab");
      return allowedTabs.has(saved) ? saved : "overview";
    } catch {
      return "overview";
    }
  });

  const handleChangeTab = (id) => {
    setActiveTab(id);
    try {
      const store = sessionStorage.getItem("idToken") ? sessionStorage : localStorage;
      store.setItem("financeActiveTab", id);
    } catch (e) {
      console.warn("Failed to persist financeActiveTab", e);
    }
  };

  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("Loading…");

  const getStore = () => {
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return localStorage;
  };

  const hardLogout = () => {
    ["idToken", "userProfile"].forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    navigate("/");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const store = getStore();
    const cachedProfile = JSON.parse(store?.getItem("userProfile") || "null");
    if (cachedProfile) {
      setProfile(cachedProfile);
      setStatus("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const init = async () => {
      const store = getStore();
      const token = store.getItem("idToken");
      if (!token) return hardLogout();

      try {
        const res = await fetch(`${BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Unauthorized");

        const me = await res.json();
        store.setItem("userProfile", JSON.stringify(me));
        setProfile(me);
        setStatus("");

        const allowedRoles = new Set(["financeAdmin", "admin", "finance"]);
        if (!allowedRoles.has(me.role)) {
          return navigate("/", { replace: true });
        }
      } catch (err) {
        console.error(err);
        hardLogout();
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "clients", label: "Pengelolahan Klien", icon: Users },
    { id: "sales-order", label: "Pengelolahan Penjualan", icon: Wallet },
    { id: "penjualan", label: "Invoice Penjualan", icon: FileText },
    { id: "tidak-aktif", label: "Invoice Tidak Aktif", icon: XCircle },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/30">
      <Header profile={profile} onLogout={hardLogout} title="Invoice Tracker" />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
        {status && <div className="text-gray-500 text-sm">{status}</div>}

        <nav className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleChangeTab(tab.id)}
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-6 py-3 md:py-4 text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-1 min-w-max relative rounded-xl
                    ${
                      isActive
                        ? "text-[#4F7CFF] bg-blue-50/50"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80"
                    }`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">
                    {tab.id === "overview" && "Overview"}
                    {tab.id === "clients" && "Clients"}
                    {tab.id === "sales-order" && "SO"}
                    {tab.id === "penjualan" && "Penjualan"}
                    {tab.id === "tidak-aktif" && "Inactive"}
                  </span>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#4F7CFF] to-transparent" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8 shadow-sm">
          {!status && (
            <>
              <div className={activeTab === "overview" ? "block" : "hidden"}>
              <FinanceOverview isActive={activeTab === "overview"} />
            </div>

                  <div className={activeTab === "clients" ? "block" : "hidden"}>
                <ClientManagement />
              </div>

              <div className={activeTab === "sales-order" ? "block" : "hidden"}>
                <SalesOrder isActive={activeTab === "sales-order"} />
              </div>

              <div className={activeTab === "penjualan" ? "block" : "hidden"}>
                <InvoicePenjualan isActive={activeTab === "penjualan"} />
              </div>

              <div className={activeTab === "tidak-aktif" ? "block" : "hidden"}>
                <InvoiceTidakAktif isActive={activeTab === "tidak-aktif"} />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
